import { useEffect, useState } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../integrations/supabase/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Home,
  Building,
  Clock,
  Shield,
  UserCheck,
  DollarSign,
  FileText,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import { Badge } from "../components/ui/badge";

interface HostelData {
  id: string;
  name: string;
  status: string;
  city: string;
  state: string;
  total_capacity: number;
  occupied_count: number;
}

interface HostelStats {
  totalCapacity: number;
  occupied: number;
  vacant: number;
  pendingApplications: number;
  trustees: number;
  fundingNeeded: number;
}

export default function HostelAdminDashboard() {
  const { user } = useAuth();
  const navigate = usePrefixedNavigate();
  const [hostel, setHostel] = useState<HostelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<HostelStats>({
    totalCapacity: 0,
    occupied: 0,
    vacant: 0,
    pendingApplications: 0,
    trustees: 0,
    fundingNeeded: 0,
  });

  useEffect(() => {
    const fetchHostelData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Fetch hostel for current admin
        const { data: hostelData, error } = await supabase
          .from("hostels")
          .select("*")
          .eq("admin_id", user.id)
          .single();

        if (error && error.code !== "PGRST116") {
          console.error("Error fetching hostel:", error);
        }

        if (hostelData) {
          setHostel(hostelData as HostelData);

          // If approved, fetch stats
          if (hostelData.status === "approved") {
            const { data: applicationsData } = await supabase
              .from("applications")
              .select("id")
              .eq("hostel_id", hostelData.id)
              .eq("status", "pending");

            const { data: trusteesData } = await supabase
              .from("trustees")
              .select("id")
              .eq("hostel_id", hostelData.id);

            setStats({
              totalCapacity: hostelData.total_capacity ?? 0,
              occupied: hostelData.occupied_count ?? 0,
              vacant:
                (hostelData.total_capacity ?? 0) -
                (hostelData.occupied_count ?? 0),
              pendingApplications: applicationsData?.length ?? 0,
              trustees: trusteesData?.length ?? 0,
              fundingNeeded: hostelData.funding_needed ?? 0,
            });
          }
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHostelData();
  }, [user]);

  const sidebarItems = [
    {
      label: "Dashboard",
      path: "/dashboard/hostel-admin",
      icon: <Home className="h-5 w-5" />,
    },
    {
      label: "Hostels",
      path: "/hostels",
      icon: <Building className="h-5 w-5" />,
    },
    {
      label: "Scholarships",
      path: "/scholarships",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      label: "Funding Status",
      path: "/funding",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      label: "Reports",
      path: "/reports",
      icon: <FileText className="h-5 w-5" />,
    },
    { label: "CMS", path: "/cms", icon: <MessageSquare className="h-5 w-5" /> },
    { label: "CRM", path: "/crm", icon: <UserCheck className="h-5 w-5" /> },
    {
      label: "Settings",
      path: "/settings",
      icon: <Shield className="h-5 w-5" />,
    },
  ];

  // No hostel registered - redirect to onboarding
  if (!loading && !hostel) {
    return (
      <DashboardLayout
        sidebarItems={sidebarItems}
        currentPath="/dashboard/hostel-admin"
        userName={user?.email ?? user?.phone ?? "Admin"}
        userPhone=""
      >
        <div className="flex min-h-[60vh] items-center justify-center">
          <Card className="w-full max-w-md text-center">
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Building className="h-8 w-8 text-primary" />
              </div>
              <CardTitle>No Hostel Registered</CardTitle>
              <CardDescription>
                You haven&apos;t registered a hostel yet. Complete your hostel
                registration to access the dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate("/hostel/onboarding")}
                className="w-full"
                size="lg"
              >
                Register Your Hostel
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Hostel pending approval
  if (!loading && hostel && hostel.status === "pending") {
    return (
      <DashboardLayout
        sidebarItems={[
          {
            label: "Dashboard",
            path: "/dashboard/hostel-admin",
            icon: <Home className="h-5 w-5" />,
          },
        ]}
        currentPath="/dashboard/hostel-admin"
        userName={user?.email ?? user?.phone ?? "Admin"}
        userPhone=""
      >
        <div className="flex min-h-[60vh] items-center justify-center">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-yellow-100">
                <Clock className="h-10 w-10 text-yellow-600" />
              </div>
              <CardTitle className="text-2xl">Pending Approval</CardTitle>
              <CardDescription className="text-base">
                Your hostel registration is under review
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3 rounded-lg bg-muted p-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Hostel Name</span>
                  <span className="font-medium">{hostel.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Location</span>
                  <span className="font-medium">
                    {hostel.city}, {hostel.state}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Capacity</span>
                  <span className="font-medium">
                    {hostel.total_capacity} students
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge
                    variant="secondary"
                    className="bg-yellow-100 text-yellow-800"
                  >
                    <Clock className="mr-1 h-3 w-3" />
                    Pending Review
                  </Badge>
                </div>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
                  <div className="text-sm text-blue-800">
                    <p className="mb-1 font-medium">What happens next?</p>
                    <ul className="space-y-1 text-blue-700">
                      <li>• Admin will review your registration details</li>
                      <li>• You may be contacted for verification</li>
                      <li>
                        • Once approved, you&apos;ll get full portal access
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Need help? Contact support at support@jbgportal.com
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Hostel rejected
  if (!loading && hostel && hostel.status === "rejected") {
    return (
      <DashboardLayout
        sidebarItems={[
          {
            label: "Dashboard",
            path: "/dashboard/hostel-admin",
            icon: <Home className="h-5 w-5" />,
          },
        ]}
        currentPath="/dashboard/hostel-admin"
        userName={user?.email ?? user?.phone ?? "Admin"}
        userPhone=""
      >
        <div className="flex min-h-[60vh] items-center justify-center">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
                <AlertCircle className="h-10 w-10 text-red-600" />
              </div>
              <CardTitle className="text-2xl">Registration Rejected</CardTitle>
              <CardDescription className="text-base">
                Your hostel registration was not approved
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-800">
                  Please contact the admin for more details about why your
                  registration was rejected and how you can reapply.
                </p>
              </div>
              <Button
                onClick={() => navigate("/hostel/onboarding")}
                className="w-full"
                size="lg"
              >
                Submit New Registration
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Approved - Full Dashboard
  return (
    <DashboardLayout
      sidebarItems={sidebarItems}
      currentPath="/dashboard/hostel-admin"
      userName={user?.email || user?.phone || "Admin"}
      userPhone=""
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="mb-2 text-3xl font-bold text-foreground">
              Hostel Dashboard
            </h2>
            <p className="text-muted-foreground">
              Welcome back! Manage your hostel operations
            </p>
          </div>
          <Badge
            variant="default"
            className="border-green-200 bg-green-100 text-green-800"
          >
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Approved
          </Badge>
        </div>

        {hostel && (
          <Card className="border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold">{hostel.name}</h3>
                  <p className="text-muted-foreground">
                    {hostel.city}, {hostel.state}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/hostels")}
                >
                  View Details
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Total Capacity
              </CardTitle>
              <Home className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {stats.totalCapacity}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Occupied</CardTitle>
              <Building className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.occupied}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Vacant</CardTitle>
              <UserCheck className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {stats.vacant}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Pending Applications
              </CardTitle>
              <Clock className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {stats.pendingApplications}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Trustees</CardTitle>
              <Shield className="h-4 w-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {stats.trustees}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">
                Funding Needed
              </CardTitle>
              <DollarSign className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                ₹{stats.fundingNeeded.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full" onClick={() => navigate("/hostels")}>
                Manage Hostel Details
              </Button>
              <Button variant="outline" className="w-full">
                Manage Applications
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/scholarships")}
              >
                View Scholarships
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                No recent activity to display
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
